import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Intestazione from "../../assets/intestazione-Giuvi-3.png";
import format from "date-fns/format";
import { it } from "date-fns/locale";

const MAIN_TABLE_FONT_SIZE = 7.2;
const MAIN_TABLE_CELL_PADDING = 0.9;
const SECONDARY_TABLE_FONT_SIZE = 7.2;
const SECONDARY_TABLE_CELL_PADDING = 1.8;
const SECTION_TITLE_FONT_SIZE = 10.8;
const HEADER_FONT_SIZE = 10.8;
const FOOTER_FONT_SIZE = 9;
const SUMMARY_FONT_SIZE = 9;

async function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = 800;
      let width = img.width;
      let height = img.height;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function toDateObject(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (ts && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts === "number") return new Date(ts);
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function isBonusActiveForMonth(bonus, periodo) {
  if (!bonus?.scadenza) return true;
  if (!periodo || typeof periodo !== "object") return true;
  if (typeof periodo.anno !== "number" || typeof periodo.mese !== "number")
    return true;
  const d = toDateObject(bonus.scadenza);
  if (!d) return true;
  const scadYear = d.getFullYear();
  const scadMonth = d.getMonth();
  if (scadYear > periodo.anno) return true;
  if (scadYear < periodo.anno) return false;
  return scadMonth >= periodo.mese;
}

function parseTimeString(timeStr, baseDate) {
  if (!timeStr || typeof timeStr !== "string") return null;
  const m = timeStr.match(/^([0-1]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  const d = baseDate instanceof Date ? new Date(baseDate) : new Date();
  d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  return d;
}

function roundEntryTimeToNextHalfHour(date) {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  if (minutes === 0) return rounded;
  if (minutes <= 30) rounded.setMinutes(30, 0, 0);
  else rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  return rounded;
}

function roundExitTimeToPreviousHalfHour(date) {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  if (minutes === 0 || minutes === 30) return rounded;
  if (minutes < 30) rounded.setMinutes(0, 0, 0);
  else rounded.setMinutes(30, 0, 0);
  return rounded;
}

function normalizeWorkDateRange(startValue, endValue) {
  const startDate = toDateObject(startValue);
  const endDate = toDateObject(endValue);

  if (!startDate || !endDate) {
    return { startDate, endDate };
  }

  const normalizedEndDate = new Date(endDate);
  if (normalizedEndDate < startDate) {
    normalizedEndDate.setDate(normalizedEndDate.getDate() + 1);
  }

  return {
    startDate,
    endDate: normalizedEndDate,
  };
}

export async function downloadPresenzeMultiPdf({
  usersData,
  periodo,
  fileName,
  projects,
  documenti,
  isAdmin = false,
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setFont("helvetica", "normal");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 15;
  const marginRight = 15;
  const marginTop = 18;

  const headerImgData = await loadImageAsDataUrl(Intestazione);
  const periodLabel =
    periodo && typeof periodo === "object"
      ? `${
          [
            "Gennaio",
            "Febbraio",
            "Marzo",
            "Aprile",
            "Maggio",
            "Giugno",
            "Luglio",
            "Agosto",
            "Settembre",
            "Ottobre",
            "Novembre",
            "Dicembre",
          ][periodo.mese]
        } ${periodo.anno}`
      : String(periodo || "N/A");

  const addHeader = (userName) => {
    const logoX = marginLeft;
    const logoY = 10;
    const logoW = 46;
    const textX = headerImgData ? logoX + logoW + 7 : marginLeft;
    const line1Y = 15;
    const line2Y = 20;
    if (headerImgData) {
      try {
        doc.addImage(headerImgData, "JPEG", logoX, logoY, logoW, 0);
      } catch (_) {}
    }
    doc.setFontSize(HEADER_FONT_SIZE);
    doc.text(`Foglio Presenze - Dipendente: ${userName}`, textX, line1Y);
    doc.text(`Periodo: ${periodLabel}`, textX, line2Y);
  };

  const addFooter = (pageNumber, totalPages) => {
    doc.setFontSize(FOOTER_FONT_SIZE);
    doc.text(
      `Pagina ${pageNumber} di ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" },
    );
  };

  let firstSection = true;
  const getCognome = (ud) =>
    String(ud?.user?.anagrafica?.cognome || "").trim() ||
    String(ud?.user?.anagrafica?.nome || "").trim() ||
    String(ud?.user?.displayName || ud?.user?.email || "").trim();

  const sortedUsersData = [...usersData].sort((a, b) =>
    getCognome(a).localeCompare(getCognome(b), "it", { sensitivity: "base" }),
  );

  for (const ud of sortedUsersData) {
    const userName =
      ud.user?.anagrafica?.nome && ud.user?.anagrafica?.cognome
        ? `${ud.user.anagrafica.nome} ${ud.user.anagrafica.cognome}`
        : ud.user?.displayName || ud.user?.email || "Utente";

    if (!firstSection) doc.addPage();
    firstSection = false;

    // Header per questa sezione
    addHeader(userName);

    // Costruzione righe e calcolo ore per utente
    const rows = (ud.presenze || []).map((item) => {
      const d = toDateObject(item.dataRegistrazione);
      const giorno = d ? format(d, "dd - eeee", { locale: it }) : "-";

      const cRaw = item.cantiere ?? item.cantiereSelezionato ?? null;
      let cantiere = "-";

      if (item.isFerie) {
        cantiere = "Ferie";
      } else if (item.isMalattia) {
        cantiere = `Malattia ${item.codiceMalattia || ""}`;
      } else if (item.isRiposo) {
        cantiere = item.note || "Riposo";
      } else {
        cantiere =
          typeof cRaw === "string"
            ? cRaw
            : cRaw?.nome || cRaw?.label || cRaw?.id || "-";
      }

      let codice = "-";
      if (cRaw && typeof cRaw === "object" && cRaw.codice) {
        codice = cRaw.codice;
      } else if (projects && Array.isArray(projects)) {
        const proj = projects.find(
          (p) => p.nome === cantiere || p.label === cantiere,
        );
        if (proj && proj.codice) codice = proj.codice;
      }

      const baseDate = d;
      const inDateRaw =
        toDateObject(item.oraIngresso) ||
        (typeof item.oraIngresso === "string"
          ? parseTimeString(item.oraIngresso, baseDate)
          : null);
      const outDateRaw =
        toDateObject(item.oraUscita) ||
        (typeof item.oraUscita === "string"
          ? parseTimeString(item.oraUscita, baseDate)
          : null);
      const inStr = inDateRaw ? format(inDateRaw, "HH:mm") : "-";
      const outStr = outDateRaw ? format(outDateRaw, "HH:mm") : "-";

      let pausa = "-";
      let ordinarie = "-";
      let viaggio = "-";
      let straordinario = "-";

      const { startDate: normalizedIngresso, endDate: normalizedUscita } =
        normalizeWorkDateRange(inDateRaw, outDateRaw);

      if (
        normalizedIngresso &&
        normalizedUscita &&
        !item.isFerie &&
        !item.isMalattia
      ) {
        const ingressoRounded =
          roundEntryTimeToNextHalfHour(normalizedIngresso);
        const uscitaRounded =
          roundExitTimeToPreviousHalfHour(normalizedUscita);
        const diffMs = uscitaRounded - ingressoRounded;
        let diffHours = diffMs / (1000 * 60 * 60);

        // Pausa
        let pausaHours = 0;
        if (item.durataPausa && item.durataPausa !== "nessuno") {
          pausaHours = parseFloat(item.durataPausa);
          if (!Number.isNaN(pausaHours)) {
            diffHours -= pausaHours;
            pausa = `${pausaHours}h`;
          }
        }
        diffHours = Math.max(0, diffHours);

        // Calcolo Ore
        const isWeekend = d && (d.getDay() === 0 || d.getDay() === 6);
        let ord = 0,
          trv = 0,
          str = 0;

        if (isWeekend) {
          str = diffHours;
        } else {
          if (diffHours <= 8) {
            ord = diffHours;
          } else if (diffHours <= 9) {
            ord = 8;
            trv = diffHours - 8;
          } else {
            ord = 8;
            trv = 1;
            str = diffHours - 9;
          }
        }
        ordinarie = ord > 0 ? `${ord.toFixed(2)}h` : "-";
        viaggio = trv > 0 ? `${trv.toFixed(2)}h` : "-";
        straordinario = str > 0 ? `${str.toFixed(2)}h` : "-";
      }
      return [
        giorno,
        inStr,
        outStr,
        pausa,
        ordinarie,
        viaggio,
        straordinario,
        codice,
        cantiere,
      ];
    });

    // Totali
    const totals = (ud.presenze || []).reduce(
      (acc, item) => {
        const d = toDateObject(item.dataRegistrazione);
        const baseDate = d;
        const inDateRaw =
          toDateObject(item.oraIngresso) ||
          (typeof item.oraIngresso === "string"
            ? parseTimeString(item.oraIngresso, baseDate)
            : null);
        const outDateRaw =
          toDateObject(item.oraUscita) ||
          (typeof item.oraUscita === "string"
            ? parseTimeString(item.oraUscita, baseDate)
            : null);

        const { startDate: normalizedIngresso, endDate: normalizedUscita } =
          normalizeWorkDateRange(inDateRaw, outDateRaw);

        if (
          normalizedIngresso &&
          normalizedUscita &&
          !item.isFerie &&
          !item.isMalattia
        ) {
          const ingressoRounded =
            roundEntryTimeToNextHalfHour(normalizedIngresso);
          const uscitaRounded =
            roundExitTimeToPreviousHalfHour(normalizedUscita);
          const diffMs = uscitaRounded - ingressoRounded;
          let diffHours = diffMs / (1000 * 60 * 60);
          if (item.durataPausa && item.durataPausa !== "nessuno") {
            const p = parseFloat(item.durataPausa);
            if (!Number.isNaN(p)) diffHours -= p;
          }
          diffHours = Math.max(0, diffHours);

          if (diffHours > 0) {
            acc.workingDays += 1;
            acc.totalHours += diffHours;
            const isWeekend = d && (d.getDay() === 0 || d.getDay() === 6);
            if (isWeekend) {
              acc.straordinario += diffHours;
              acc.weekendStraord += diffHours;
            } else {
              if (diffHours <= 8) {
                acc.ordinarie += diffHours;
              } else if (diffHours <= 9) {
                acc.ordinarie += 8;
                acc.viaggio += diffHours - 8;
              } else {
                acc.ordinarie += 8;
                acc.viaggio += 1;
                acc.straordinario += diffHours - 9;
              }
            }
          }
        }
        return acc;
      },
      {
        totalHours: 0,
        workingDays: 0,
        ordinarie: 0,
        viaggio: 0,
        straordinario: 0,
        weekendStraord: 0,
      },
    );

    autoTable(doc, {
      startY: marginTop + 18,
      head: [
        [
          "Data",
          "In.",
          "Out",
          "Pausa",
          "Ord.",
          "Viag.",
          "Str.",
          "Codice",
          "Cantiere",
        ],
      ],
      body: rows,
      theme: "grid",
      styles: {
        fontSize: MAIN_TABLE_FONT_SIZE,
        cellPadding: MAIN_TABLE_CELL_PADDING,
        halign: "center",
        valign: "middle",
      },
      headStyles: { fillColor: [161, 16, 0], textColor: 255, halign: "center" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 26, halign: "left" },
        7: { cellWidth: 18, halign: "center" },
        8: { cellWidth: "auto", halign: "left" },
      },
      margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
      didDrawPage: (data) => {
        addHeader(userName);
      },
    });

    // Reperibilità Table
    if (ud.reperibilita && ud.reperibilita.length > 0) {
      const repRows = ud.reperibilita.reduce((acc, item) => {
        const start = toDateObject(item.dataInizio);
        const end = toDateObject(item.dataFine);
        const startStr = start ? format(start, "dd/MM/yyyy") : "-";
        const endStr = end ? format(end, "dd/MM/yyyy") : "-";

        let duration = "-";
        if (start && end) {
          const diffTime = end - start;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
          duration = `${diffDays} giorni`;
        }

        // Calcolo ore interventi
        let interventionHours = 0;
        if (item.interventi && Array.isArray(item.interventi)) {
          item.interventi.forEach((int) => {
            if (int.oraInizio && int.oraFine) {
              const s = parseTimeString(int.oraInizio);
              const e = parseTimeString(int.oraFine);
              if (s && e) {
                let diff = (e - s) / (1000 * 60 * 60);
                if (diff < 0) diff += 24;
                interventionHours += diff;
              }
            }
          });
        }

        // Aggiorna totali globali
        if (interventionHours > 0) {
          totals.straordinario += interventionHours;
          totals.totalHours += interventionHours;
        }

        // Main row
        let noteText = item.note || "-";
        if (interventionHours > 0) {
          const extraText = `Tot. Int.: ${interventionHours.toFixed(2)}h`;
          noteText =
            noteText === "-" || !noteText
              ? extraText
              : `${noteText} - ${extraText}`;
        }

        acc.push([startStr, endStr, duration, noteText]);

        // Interventions rows
        if (item.interventi && Array.isArray(item.interventi)) {
          item.interventi.forEach((int) => {
            let dStr = "-";
            if (
              typeof int.data === "string" &&
              /^\d{4}-\d{2}-\d{2}$/.test(int.data)
            ) {
              const [y, m, d] = int.data.split("-");
              dStr = `${d}/${m}/${y}`;
            } else {
              const d = toDateObject(int.data);
              dStr = d ? format(d, "dd/MM/yyyy") : "-";
            }
            const orario = `${int.oraInizio || ""} - ${int.oraFine || ""}`;

            acc.push([`   Intervento: ${dStr}`, orario, int.luogo || "-", ""]);
          });
        }

        return acc;
      }, []);

      let finalY = doc.lastAutoTable.finalY || marginTop + 18;
      if (finalY > pageHeight - 48) {
        doc.addPage();
        addHeader(userName);
        finalY = marginTop;
      }

      doc.setFontSize(SECTION_TITLE_FONT_SIZE);
      doc.setTextColor(0, 0, 0);
      doc.text("Reperibilità", marginLeft, finalY + 8);

      autoTable(doc, {
        startY: finalY + 10,
        head: [["Data Inizio", "Data Fine", "Durata", "Note"]],
        body: repRows,
        theme: "grid",
        styles: {
          fontSize: SECONDARY_TABLE_FONT_SIZE,
          cellPadding: SECONDARY_TABLE_CELL_PADDING,
          halign: "left",
        },
        headStyles: {
          fillColor: [161, 16, 0],
          textColor: 255,
          halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
        didDrawPage: (data) => {
          addHeader(userName);
        },
      });
    }

    // Bonus Table
    let bonusList = Array.isArray(ud.user?.anagrafica?.bonus)
      ? ud.user.anagrafica.bonus
      : [];

    if (ud.confirmedBonuses !== null && Array.isArray(ud.confirmedBonuses)) {
      bonusList = ud.confirmedBonuses;
    }

    bonusList = bonusList.filter((b) => isBonusActiveForMonth(b, periodo));

    if (bonusList.length > 0) {
      const bonusRows = bonusList.map((item) => {
        const importo = item.importo
          ? new Intl.NumberFormat("it-IT", {
              style: "currency",
              currency: "EUR",
            }).format(item.importo)
          : "-";
        let scadenza = "-";
        if (item.scadenza) {
          const d = toDateObject(item.scadenza);
          if (d) scadenza = format(d, "dd/MM/yyyy");
        }
        return [
          item.nome || "-",
          item.descrizione || "-",
          importo,
          scadenza,
          "",
        ];
      });

      let finalY = doc.lastAutoTable.finalY || marginTop + 18;
      if (finalY > pageHeight - 48) {
        doc.addPage();
        addHeader(userName);
        finalY = marginTop;
      }

      doc.setFontSize(SECTION_TITLE_FONT_SIZE);
      doc.setTextColor(0, 0, 0);
      doc.text("Bonus", marginLeft, finalY + 8);

      autoTable(doc, {
        startY: finalY + 10,
        head: [["Nome", "Descrizione", "Importo", "Scadenza", "Assegnare"]],
        body: bonusRows,
        theme: "grid",
        styles: {
          fontSize: SECONDARY_TABLE_FONT_SIZE,
          cellPadding: SECONDARY_TABLE_CELL_PADDING,
          halign: "left",
        },
        headStyles: {
          fillColor: [161, 16, 0],
          textColor: 255,
          halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
        didDrawPage: (data) => {
          addHeader(userName);
        },
      });
    }

    // Addebiti Personali Table
    const mesi = [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ];
    const nomeMese =
      periodo && typeof periodo.mese === "number" ? mesi[periodo.mese] : "";
    const annoStr = periodo ? String(periodo.anno) : "";

    const addebitiList = (documenti || []).filter((doc) => {
      if (
        doc.tipoDocumento !== "addebitoPersonale" ||
        doc.owner !== ud.user.id
      ) {
        return false;
      }

      // Check 1: Legacy string match
      if (
        String(doc.anno) === annoStr &&
        doc.competenza &&
        doc.competenza.toLowerCase() === nomeMese.toLowerCase()
      ) {
        return true;
      }

      // Check 2: Date object match
      if (doc.data) {
        const docDate = toDateObject(doc.data);
        if (
          docDate &&
          periodo &&
          typeof periodo.mese === "number" &&
          periodo.anno
        ) {
          return (
            docDate.getFullYear() === periodo.anno &&
            docDate.getMonth() === periodo.mese
          );
        }
      }

      return false;
    });

    let totaleAddebiti = 0;

    if (addebitiList.length > 0) {
      const addebitiRows = addebitiList.map((item) => {
        const importoVal = parseFloat(item.importo) || 0;
        totaleAddebiti += importoVal;

        const importoFmt = new Intl.NumberFormat("it-IT", {
          style: "currency",
          currency: "EUR",
        }).format(importoVal);

        return [item.identificativo || "Addebito", importoFmt];
      });

      // Add total row
      addebitiRows.push([
        { content: "Totale", styles: { fontStyle: "bold", halign: "right" } },
        {
          content: new Intl.NumberFormat("it-IT", {
            style: "currency",
            currency: "EUR",
          }).format(totaleAddebiti),
          styles: { fontStyle: "bold", halign: "right" },
        },
      ]);

      let finalY = doc.lastAutoTable.finalY || marginTop + 18;
      if (finalY > pageHeight - 46) {
        doc.addPage();
        addHeader(userName);
        finalY = marginTop;
      }

      doc.setFontSize(SECTION_TITLE_FONT_SIZE - 1.8);
      doc.setTextColor(0, 0, 0);
      doc.text("Addebiti Personali", marginLeft, finalY + 8);

      autoTable(doc, {
        startY: finalY + 10,
        head: [["Descrizione", "Importo"]],
        body: addebitiRows,
        theme: "grid",
        styles: {
          fontSize: MAIN_TABLE_FONT_SIZE,
          cellPadding: SECONDARY_TABLE_CELL_PADDING,
          halign: "left",
        },
        headStyles: {
          fillColor: [161, 16, 0],
          textColor: 255,
          halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          1: { halign: "right", cellWidth: 40 },
        },
        margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
        didDrawPage: (data) => {
          addHeader(userName);
        },
      });
    }

    // Totali
    const finalY = doc.lastAutoTable?.finalY || marginTop + 18;
    const summaryY = finalY + 10;
    const moneyHeight = 20; // Spazio per 3 righe compattate

    // Se non c'è spazio, nuova pagina
    if (summaryY + moneyHeight > pageHeight - 16) {
      doc.addPage();
      addHeader(userName);
    }

    // Posizione fissa in basso o subito dopo?
    // Meglio subito dopo per multi-user, o in basso alla pagina corrente.
    // Usiamo posizione relativa ma con check pagina.

    const printY =
      summaryY + moneyHeight > pageHeight - 16 ? marginTop + 18 : summaryY;

    const breakdownY = printY + 8;
    const totalY = printY + 16;

    doc.setFontSize(SUMMARY_FONT_SIZE);
    let xPos = marginLeft;
    doc.text(`Giorni: ${totals.workingDays}`, xPos, printY);
    xPos += 30;
    doc.text(`Ord: ${totals.ordinarie.toFixed(2)}h`, xPos, printY);
    xPos += 30;
    doc.text(`Viag: ${totals.viaggio.toFixed(2)}h`, xPos, printY);
    xPos += 30;
    doc.text(`Str: ${totals.straordinario.toFixed(2)}h`, xPos, printY);

    const totalStr = `TOT: ${totals.totalHours.toFixed(2)}h`;
    doc.text(totalStr, pageWidth - marginRight, printY, { align: "right" });

    // --- Nuova tabulazione per importi (Richiesta User) ---
    const anagrafica = ud.user?.anagrafica || {};
    const valPasto = parseFloat(anagrafica.pasto) || 0;
    const valViaggio = parseFloat(anagrafica.oreViaggio) || 0;
    const valStraord = parseFloat(anagrafica.oreStraordinarie) || 0;
    const tipoStipendio = anagrafica.tipoStipendio || "Contratto"; // Valore di default

    const importoPasti = totals.workingDays * valPasto;
    const importoViaggio = totals.viaggio * valViaggio;
    const importoStraord = totals.straordinario * valStraord;
    const importoFisso = parseFloat(anagrafica.importoFisso) || 0;

    // Calcolo Bonus Totale (somma di tutti i bonus validi mostrati in tabella)
    let totaleBonus = 0;
    if (bonusList.length > 0) {
      bonusList.forEach((item) => {
        totaleBonus += parseFloat(item.importo) || 0;
      });
    }

    // Calcolo Totale Addebiti
    // (già calcolato sopra nella variabile totaleAddebiti durante la generazione della tabella)

    // Calcolo Reperibilità (giorni * 10€)
    let importoReperibilita = 0;
    if (ud.reperibilita && ud.reperibilita.length > 0) {
      let giorniReperibilita = 0;
      const monthStart =
        periodo && typeof periodo === "object"
          ? new Date(periodo.anno, periodo.mese, 1)
          : null;
      const monthEnd =
        periodo && typeof periodo === "object"
          ? new Date(periodo.anno, periodo.mese + 1, 0)
          : null;

      if (monthStart && monthEnd) {
        ud.reperibilita.forEach((rep) => {
          const repStart = toDateObject(rep.dataInizio);
          const repEnd = toDateObject(rep.dataFine);

          if (repStart && repEnd) {
            const start = repStart < monthStart ? monthStart : repStart;
            const end = repEnd > monthEnd ? monthEnd : repEnd;

            if (start <= end) {
              const s = new Date(start);
              s.setHours(0, 0, 0, 0);
              const e = new Date(end);
              e.setHours(0, 0, 0, 0);
              const diffTime = Math.abs(e - s);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
              giorniReperibilita += diffDays;
            }
          }
        });
      }
      importoReperibilita = giorniReperibilita * 10;
    }

    const formatEuro = (val) =>
      new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(val);

    xPos = marginLeft;

    doc.setFont("helvetica", "bold");

    // Calcolo Totale Rimborsi in base al tipo stipendio
    // Formula generale: Base + (Eventuali Extra) + Bonus + Reperibilità - Addebiti

    let totaleRimborsi = 0;

    if (isAdmin) {
      // Gestione visualizzazione in base a tipoStipendio
      if (tipoStipendio === "Fisso") {
        // Fisso: Importo Fisso + Reperibilità + Bonus - Addebiti
        totaleRimborsi =
          importoFisso + importoReperibilita + totaleBonus - totaleAddebiti;

        doc.text(`Pasti: -`, xPos, breakdownY);
        doc.text(`Fisso: ${formatEuro(importoFisso)}`, xPos + 35, breakdownY);
        doc.text(
          `Reperib.: ${formatEuro(importoReperibilita)}`,
          xPos + 75,
          breakdownY,
        );
        doc.text(`Bonus: ${formatEuro(totaleBonus)}`, xPos + 115, breakdownY);
      } else if (tipoStipendio === "Fisso+Sabato") {
        // Fisso+Sabato (Logica da ValorizationModal: Fisso + StraordWE + Reperibilità + Bonus - Addebiti)
        const importoStraordWE = (totals.weekendStraord || 0) * valStraord;
        totaleRimborsi =
          importoFisso +
          importoStraordWE +
          importoReperibilita +
          totaleBonus -
          totaleAddebiti;

        doc.text(`Pasti: -`, xPos, breakdownY);
        doc.text(`Fisso: ${formatEuro(importoFisso)}`, xPos + 35, breakdownY);
        doc.text(
          `Str.WE: ${formatEuro(importoStraordWE)}`,
          xPos + 75,
          breakdownY,
        );
        doc.text(
          `Reperib.: ${formatEuro(importoReperibilita)}`,
          xPos + 115,
          breakdownY,
        );
        doc.text(`Bonus: ${formatEuro(totaleBonus)}`, xPos + 155, breakdownY);
      } else if (tipoStipendio === "Fisso+Straordinari") {
        // Fisso+Straordinari: Fisso + Pasti + Viaggio + Straordinari + Reperibilità + Bonus - Addebiti
        totaleRimborsi =
          importoFisso +
          importoPasti +
          importoViaggio +
          importoStraord +
          importoReperibilita +
          totaleBonus -
          totaleAddebiti;

        doc.text(`Pasti: ${formatEuro(importoPasti)}`, xPos, breakdownY);
        doc.text(`Fisso: ${formatEuro(importoFisso)}`, xPos + 35, breakdownY);
        doc.text(
          `Viag/Str: ${formatEuro(importoViaggio + importoStraord)}`,
          xPos + 75,
          breakdownY,
        );
        doc.text(
          `Reperib.: ${formatEuro(importoReperibilita)}`,
          xPos + 115,
          breakdownY,
        );
        doc.text(`Bonus: ${formatEuro(totaleBonus)}`, xPos + 155, breakdownY);
      } else {
        // Contratto (Default): Pasti + Viaggio + Straordinari + Reperibilità + Bonus - Addebiti
        totaleRimborsi =
          importoPasti +
          importoViaggio +
          importoStraord +
          importoReperibilita +
          totaleBonus -
          totaleAddebiti;

        doc.text(`Pasti: ${formatEuro(importoPasti)}`, xPos, breakdownY);
        doc.text(
          `Viaggio: ${formatEuro(importoViaggio)}`,
          xPos + 35,
          breakdownY,
        );
        doc.text(
          `Straord: ${formatEuro(importoStraord)}`,
          xPos + 70,
          breakdownY,
        );
        doc.text(
          `Reperib.: ${formatEuro(importoReperibilita)}`,
          xPos + 105,
          breakdownY,
        );
        doc.text(`Bonus: ${formatEuro(totaleBonus)}`, xPos + 140, breakdownY);
      }

      // Addebiti (mostrato per tutti) e Totale Finale
      // Spostiamo Addebiti e Totale su una nuova riga (totalY) per evitare sovrapposizioni

      const labelAddebiti = `Addebiti: -${formatEuro(totaleAddebiti)}`;
      const labelTotale = `TOT: ${formatEuro(totaleRimborsi)}`;

      // Calcoliamo larghezze per allineamento a destra
      const widthTotale = doc.getTextWidth(labelTotale);

      // Posizionamento a destra
      doc.text(labelTotale, pageWidth - marginRight, totalY, {
        align: "right",
      });
      doc.text(
        labelAddebiti,
        pageWidth - marginRight - widthTotale - 10,
        totalY,
        { align: "right" },
      ); // 10mm padding
    }

    doc.setFont("helvetica", "normal");
    // -----------------------------------------------------
  }

  // Imposta footer globale con totale pagine definitivo
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  let finalName = fileName || `presenze_tutti_dipendenti_${periodLabel}.pdf`;
  doc.save(finalName);
}
